import { useState, useEffect, useRef, useCallback } from "react";
import {
  FaPhoneAlt,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaTag,
  FaConciergeBell,
  FaImages,
  FaBuilding,
  FaQuestionCircle,
} from "react-icons/fa";
import { uploadsUrl } from "../utils/api";
import { DEFAULT_PHONE, telHref } from "../utils/constants";
import "./Navbar.css";

export default function Navbar({
  projectId,
  projectName,
  authorizedChannelPartner,
  onCtaClick,
  phone,
}) {
  const callNumber = phone || DEFAULT_PHONE;
  const callHref = telHref(callNumber);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const togglerRef = useRef(null);
  const firstLinkRef = useRef(null);
  const didMountRef = useRef(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Every link below is a real <a href="#id">, so clicking one already
  // gives it its own unique, shareable/bookmarkable URL and scrolls there
  // via the browser's native smooth-scroll (see html { scroll-behavior:
  // smooth } in global.css) — no JS scrolling needed. This just closes
  // the mobile sheet so it doesn't stay open over the section it jumped to.
  const closeMenu = useCallback(() => setOpen(false), []);

  // About / Location / Price / Amenities / Gallery / Developer / FAQ, each
  // pointing at the matching section id (see AboutSection.jsx,
  // LocationSection.jsx, ApartmentSection.jsx, AmenitiesSection.jsx,
  // GallerySection.jsx, DeveloperSection.jsx, FAQSection.jsx).
  // (SiteVisitSection #sitevisit and ContactUsSection #contactus exist on
  // the page and are reachable directly via URL, just not linked here.)
  const navLinks = [
    { href: "#about", label: "About", icon: FaInfoCircle },
    { href: "#location", label: "Location", icon: FaMapMarkerAlt },
    { href: "#pricing", label: "Price", icon: FaTag },
    { href: "#amenities", label: "Amenities", icon: FaConciergeBell },
    { href: "#gallery", label: "Gallery", icon: FaImages },
    { href: "#developer", label: "Developer", icon: FaBuilding },
    { href: "#faq", label: "FAQ", icon: FaQuestionCircle },
  ];

  // Mobile menu open/close side-effects — scroll lock, Escape-to-close and
  // focus handoff. Previously the menu was purely visual: the page kept
  // scrolling underneath it, there was nothing to tap outside it to
  // dismiss it, and focus never moved in or back out. Skipped on the very
  // first render so mount doesn't yank focus onto the toggler.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (open) {
      // iOS-safe scroll lock: `overflow: hidden` alone still allows
      // rubber-band scrolling under the fixed panel on Safari, so the
      // body is pinned in place instead and the scroll position is
      // restored on close.
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";

      firstLinkRef.current?.focus();

      const onKeyDown = (e) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }

    const storedScrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    if (storedScrollY) {
      window.scrollTo(0, parseInt(storedScrollY, 10) * -1);
    }
    togglerRef.current?.focus();
  }, [open]);

  // Safety net: if the component unmounts while the menu happens to be
  // open, don't leave the page permanently scroll-locked.
  useEffect(() => {
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, []);

  return (
    <nav className={`navbar${scrolled ? " scrolled" : ""}`} aria-label="Main navigation">
      <div className="navbar__inner">

        {/* Logo */}
        <a className="navbar__brand" href="/" aria-label={`${projectName} home`}>
          <img
            src={uploadsUrl(projectId, "logo.webp")}
            alt={`${projectName} Logo`}
            className="navbar__logo"
            width="160"
            height="45"
            loading="eager"
          />
        </a>

        {/* ✅ Always visible — outside collapse */}
        {authorizedChannelPartner && (
          <div className="navbar__channel-partner">
            <img
              src="https://microsite.thehomestore.co.in/uploads/homestore_logo.png"
              alt="The Homestore"
              className="navbar__logo navbar__logo--partner"
            />
            <span className="navbar__partner-label">
              Authorised Channel Partner
            </span>
          </div>
        )}

        {/* Mobile-only group: phone icon + hamburger, pinned together as
            one unit at the right edge (see .navbar__mobile-actions in
            Navbar.css) rather than each managing its own right-alignment
            independently. */}
        <div className="navbar__mobile-actions">
          {/* Mobile phone icon — react-icons glyph + a CSS pulse ring
              stands in for the old attention-grabbing phone_animation.gif */}
          <a href={callHref} className="navbar__phone-icon" aria-label="Call us">
            <span className="navbar__phone-icon-ring" aria-hidden="true" />
            <FaPhoneAlt aria-hidden="true" />
          </a>

          {/* Hamburger — 44×44 tap target wrapping a visually smaller icon,
              so the touch area meets accessibility guidelines without
              enlarging the glyph itself. */}
          <button
            ref={togglerRef}
            type="button"
            className={`navbar__toggler${open ? " open" : ""}`}
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="navMenu"
          >
            <span className="navbar__toggler-bars">
              <span /><span /><span />
            </span>
          </button>
        </div>

        {/* Backdrop — dims and blocks the page behind the open mobile
            menu, and tapping it (like Escape) closes the menu. Without
            it the panel looked like it was floating over live, tappable
            content instead of a temporary overlay. */}
        <div
          className={`navbar__backdrop${open ? " show" : ""}`}
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />

        {/* Nav links — each is a real #id anchor, so every one opens its
            own unique URL (shareable, bookmarkable, back-button-safe)
            and the browser's native smooth-scroll carries the page
            there; no click handler needed for the scroll/URL itself. */}
        <div className={`navbar__collapse${open ? " show" : ""}`} id="navMenu">
          <ul className="navbar__nav">
            {navLinks.map(({ href, label, icon: Icon }, i) => (
              <li key={href}>
                <a
                  href={href}
                  className="navbar__link"
                  onClick={closeMenu}
                  ref={i === 0 ? firstLinkRef : undefined}
                >
                  <Icon aria-hidden="true" className="navbar__nav-icon" />
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="navbar__cta-group">
          {/* Desktop-only phone number — mobile already has the round
              phone icon button above, so this is scoped to >=992px
              (see .navbar__call in Navbar.css). "Book A Site Visit" was
              dropped from the navbar itself; Hero still has its own CTA
              for that. */}
          <a href={callHref} className="navbar__call">
            <FaPhoneAlt aria-hidden="true" className="navbar__call-icon" />
            <span>{callNumber}</span>
          </a>
        </div>

      </div>
    </nav>
  );
}